import React from 'react';
import { useParams } from 'react-router-dom';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
  IonContent,
  IonCard,
  IonCardContent,
  IonSegment,
  IonSegmentButton,
  IonLabel,
  IonSpinner,
  IonText,
  IonIcon,
  IonSkeletonText,
} from '@ionic/react';
import {
  thermometerOutline,
  waterOutline,
  rainyOutline,
  compassOutline,
  arrowDownOutline,
  arrowUpOutline,
  analyticsOutline,
  swapVerticalOutline,
  calendarOutline,
  sunnyOutline,
  flagOutline,
  speedometerOutline,
  leafOutline,
  pulseOutline,
} from 'ionicons/icons';
import {
  STATION_DETAIL_META,
  STATION_DETAIL_SLUGS,
  STATION_RANGE_LABELS,
  isHourlyRange,
  apparentTemperature,
  beaufort,
  compassName,
  compassPoint,
  compassSectorLabel,
  daysSinceRain,
  deltaT,
  dewPoint,
  formatRelative,
  rainIntensityLabel,
  rainyDays,
  round1,
  pressureTrend,
  soilMoistureLabel,
  type SoilProbeOverview,
  stationInsights,
  type StationDetailSlug,
  type StationBucket,
  type StationRange,
  type WeatherStationOverview,
} from '../../domain/WeatherStation';
import type { AgroForecast } from '../../domain/Weather';
import { useWeatherStation } from '../../application/use-cases/weatherStation/useWeatherStation';
import { BandChart, OutageLegend, RainBars, WindChart, timelineOf } from '../components/station/StationCharts';
import { ConnectivityBanner } from '../components/ConnectivityStatus';
import { ForecastHours } from '../components/station/StationForecast';
import CountUp from '../components/CountUp';
import StationInsights from '../components/station/StationInsights';
import WindCompass from '../components/station/WindCompass';
import WindRose from '../components/station/WindRose';
import '../components/station/station.css';

const ICON_BY_SLUG: Record<StationDetailSlug, string> = {
  temperatura: thermometerOutline,
  humedad: waterOutline,
  presion: speedometerOutline,
  lluvia: rainyOutline,
  viento: compassOutline,
};

const METRIC_CLASS: Record<StationDetailSlug, string> = {
  temperatura: 'metric-temp',
  humedad: 'metric-humidity',
  presion: 'metric-pressure',
  lluvia: 'metric-rain',
  viento: 'metric-wind',
};

function formatAt(iso: string, range: StationRange): string {
  const d = new Date(iso);
  const time = d.toLocaleTimeString('es-AR', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  if (isHourlyRange(range)) return `a las ${time}`;
  return `${d.toLocaleDateString('es-AR', { weekday: 'short', day: '2-digit', month: '2-digit' })} ${time}`;
}

const RANGE_TITLE: Record<StationRange, string> = {
  '6h': 'Últimas 6 horas',
  '12h': 'Últimas 12 horas',
  '24h': 'Últimas 24 horas',
  '7d': 'Últimos 7 días',
  '30d': 'Últimos 30 días',
};

interface StatCell {
  icon: string;
  label: string;
  value: string;
  hint?: string;
}

const StatsGrid: React.FC<{ cells: StatCell[] }> = ({ cells }) => (
  <div className="station-stats">
    {cells.map((cell) => (
      <div className="station-stat" key={cell.label}>
        <IonIcon icon={cell.icon} />
        <div>
          <p className="station-stat-label">{cell.label}</p>
          <p className="station-stat-value">{cell.value}</p>
          {cell.hint && <p className="station-stat-hint">{cell.hint}</p>}
        </div>
      </div>
    ))}
  </div>
);

function heroFor(slug: StationDetailSlug, o: WeatherStationOverview): { value: string; unit: string; sub: string } {
  const t = o.current.temperature?.value ?? null;
  const rh = o.current.humidity?.value ?? null;
  const w = o.current.windSpeed?.value ?? null;
  const dir = o.current.windDirection?.value ?? null;
  switch (slug) {
    case 'temperatura':
      return {
        value: t !== null ? `${round1(t)}` : '—',
        unit: '°C',
        sub: t !== null && rh !== null ? `Sensación térmica ${Math.round(apparentTemperature(t, rh, w ?? 0))}°` : '',
      };
    case 'humedad':
      return {
        value: rh !== null ? `${Math.round(rh)}` : '—',
        unit: '%',
        sub:
          t !== null && rh !== null
            ? `Punto de rocío ${round1(dewPoint(t, rh))}° · Delta T ${round1(deltaT(t, rh))}`
            : '',
      };
    case 'presion': {
      const p = o.current.pressure?.value ?? null;
      const trend = pressureTrend(o.series.pressure, o.current.pressure);
      return {
        value: p !== null ? `${Math.round(p)}` : '—',
        unit: 'hPa',
        sub: trend ? `${trend.label} (${trend.delta > 0 ? '+' : ''}${trend.delta} hPa en 3 h) · ${trend.hint}` : '',
      };
    }
    case 'lluvia':
      return {
        value: `${o.rain.today}`,
        unit: 'mm hoy',
        sub:
          rainIntensityLabel(o.rain.lastHour) +
          (o.rain.lastHour > 0 ? ` · ${o.rain.lastHour} mm en la última hora` : ''),
      };
    case 'viento':
      return {
        value: w !== null ? `${Math.round(w)}` : '—',
        unit: 'km/h',
        sub: [
          dir !== null ? `Del ${compassName(dir).toLowerCase()} (${compassPoint(dir)}, ${Math.round(dir)}°)` : null,
          w !== null ? beaufort(w).name : null,
        ]
          .filter(Boolean)
          .join(' · '),
      };
  }
}

function statsFor(slug: StationDetailSlug, o: WeatherStationOverview): StatCell[] {
  const range = o.range;
  if (slug === 'temperatura' || slug === 'humedad') {
    const s = slug === 'temperatura' ? o.stats.temperature : o.stats.humidity;
    const unit = slug === 'temperatura' ? '°C' : '%';
    if (!s) return [];
    const cells: StatCell[] = [
      {
        icon: arrowDownOutline,
        label: 'Mínima',
        value: `${round1(s.min)}${unit}`,
        hint: formatAt(s.minAt, range),
      },
      {
        icon: arrowUpOutline,
        label: 'Máxima',
        value: `${round1(s.max)}${unit}`,
        hint: formatAt(s.maxAt, range),
      },
      {
        icon: analyticsOutline,
        label: 'Promedio',
        value: `${round1(s.avg)}${unit}`,
      },
    ];
    if (slug === 'temperatura') {
      cells.push({
        icon: swapVerticalOutline,
        label: 'Amplitud térmica',
        value: `${round1(s.max - s.min)}°`,
      });
    } else {
      const t = o.current.temperature?.value ?? null;
      const rh = o.current.humidity?.value ?? null;
      cells.push({
        icon: waterOutline,
        label: 'Punto de rocío',
        value: t !== null && rh !== null ? `${round1(dewPoint(t, rh))}°C` : '—',
        hint: 'por debajo, condensa',
      });
    }
    return cells;
  }

  if (slug === 'presion') {
    const s = o.stats.pressure;
    if (!s) return [];
    const trend = pressureTrend(o.series.pressure, o.current.pressure);
    return [
      { icon: arrowDownOutline, label: 'Mínima', value: `${round1(s.min)} hPa`, hint: formatAt(s.minAt, range) },
      { icon: arrowUpOutline, label: 'Máxima', value: `${round1(s.max)} hPa`, hint: formatAt(s.maxAt, range) },
      { icon: analyticsOutline, label: 'Promedio', value: `${round1(s.avg)} hPa` },
      {
        icon: swapVerticalOutline,
        label: 'Tendencia (3 h)',
        value: trend ? `${trend.delta > 0 ? '+' : ''}${trend.delta} hPa` : '—',
        hint: trend?.label.toLowerCase(),
      },
    ];
  }

  if (slug === 'lluvia') {
    const days = range === '7d' ? o.rain.daily.slice(-7) : o.rain.daily;
    const total =
      range === '7d'
        ? o.rain.last7d
        : range === '30d'
          ? o.rain.last30d
          : range === '24h'
            ? o.rain.last24h
            : round1(o.series.rain.reduce((acc, b) => acc + b.sum, 0));
    const wettest = days.reduce<{ date: string; mm: number } | null>(
      (best, d) => (!best || d.mm > best.mm ? d : best),
      null
    );
    const since = daysSinceRain(o.rain.daily);
    const sinceCell: StatCell = {
      icon: sunnyOutline,
      label: 'Sin llover',
      value: since === null ? 'Más de 30 días' : since === 0 ? 'Llovió hoy' : `${since} día${since > 1 ? 's' : ''}`,
    };
    // En rangos de horas la ventana es móvil (no días calendario): en vez de
    // "días con lluvia" tiene más sentido cuánto llovió hoy y la intensidad pico.
    if (isHourlyRange(range)) {
      const peak = o.series.rain.reduce<StationBucket | null>(
        (best, b) => (!best || b.sum > best.sum ? b : best),
        null
      );
      const peakMmH = peak ? round1(peak.sum * (3600 / o.bucketSeconds)) : 0;
      return [
        { icon: rainyOutline, label: `Últimas ${STATION_RANGE_LABELS[range]}`, value: `${total} mm` },
        { icon: calendarOutline, label: 'Hoy', value: `${o.rain.today} mm` },
        {
          icon: arrowUpOutline,
          label: 'Intensidad pico',
          value: peak && peak.sum > 0 ? `${peakMmH} mm/h` : '—',
          hint:
            peak && peak.sum > 0
              ? `${rainIntensityLabel(peakMmH).toLowerCase()} · ${formatAt(peak.t, range)}`
              : undefined,
        },
        sinceCell,
      ];
    }
    return [
      { icon: rainyOutline, label: 'Total del período', value: `${total} mm` },
      {
        icon: calendarOutline,
        label: 'Días con lluvia',
        value: `${rainyDays(days)} de ${days.length}`,
      },
      {
        icon: arrowUpOutline,
        label: 'Día más lluvioso',
        value: wettest && wettest.mm > 0 ? `${wettest.mm} mm` : '—',
        hint: wettest && wettest.mm > 0 ? wettest.date.split('-').reverse().slice(0, 2).join('/') : undefined,
      },
      sinceCell,
    ];
  }

  const s = o.stats.windSpeed;
  const dominant = o.windRose.reduce((best, sector) => (sector.count > best.count ? sector : best), o.windRose[0]);
  return [
    {
      icon: analyticsOutline,
      label: 'Promedio',
      value: s ? `${round1(s.avg)} km/h` : '—',
    },
    {
      icon: speedometerOutline,
      label: 'Ráfaga máxima',
      value: s ? `${Math.round(s.max)} km/h` : '—',
      hint: s ? formatAt(s.maxAt, range) : undefined,
    },
    {
      icon: flagOutline,
      label: 'Dirección dominante',
      value: dominant && dominant.count > 0 ? compassSectorLabel(dominant.sector) : '—',
    },
    {
      icon: compassOutline,
      label: 'Escala Beaufort (ráfaga)',
      value: s ? `${beaufort(s.max).force} · ${beaufort(s.max).name}` : '—',
    },
  ];
}

const ForecastSection: React.FC<{
  slug: StationDetailSlug;
  forecast: AgroForecast | null;
  status: string;
}> = ({ slug, forecast, status }) => {
  if (status === 'unset') {
    return (
      <p className="station-empty">
        La estación no tiene ubicación todavía: marcala en el mapa desde su detalle (o asignale un campo) para ver el
        pronóstico de ese lugar.
      </p>
    );
  }
  if (status === 'error') return <p className="station-empty">No se pudo obtener el pronóstico.</p>;
  if (!forecast)
    return <IonSkeletonText animated style={{ width: '100%', height: 160, borderRadius: 10, margin: 0 }} />;

  if (slug === 'lluvia') {
    return (
      <>
        <WaterBalance forecast={forecast} />
        <ForecastHours hours={forecast.hourly} metric="rain" />
      </>
    );
  }
  if (slug === 'viento') {
    return (
      <>
        <ForecastHours hours={forecast.hourly} metric="wind" />
      </>
    );
  }
  if (slug === 'humedad') return <ForecastHours hours={forecast.hourly} metric="humidity" />;
  return <ForecastHours hours={forecast.hourly} metric="temperature" />;
};

function soilStats(probe: SoilProbeOverview, range: StationRange): StatCell[] {
  const s = probe.stats;
  if (!s) return [];
  return [
    {
      icon: arrowDownOutline,
      label: 'Mínima',
      value: `${round1(s.min)}%`,
      hint: formatAt(s.minAt, range),
    },
    {
      icon: arrowUpOutline,
      label: 'Máxima',
      value: `${round1(s.max)}%`,
      hint: formatAt(s.maxAt, range),
    },
    { icon: analyticsOutline, label: 'Promedio', value: `${round1(s.avg)}%` },
    {
      icon: swapVerticalOutline,
      label: 'Variación',
      value: `${round1(s.max - s.min)} pts`,
    },
  ];
}

// Sonda de suelo: misma pantalla que las demás métricas, con la serie de
// esa sonda y el balance de lluvia vs. evapotranspiración del pronóstico
// (lo que va a pasar con la humedad del suelo en los próximos días).
const SoilDetail: React.FC<{ id: string; channel: number }> = ({ id, channel }) => {
  const { device, overview, range, setRange, loading, rangeLoading, error, forecast, forecastStatus, latestAt } =
    useWeatherStation(id);
  const probe = overview?.soil.find((p) => p.channel === channel) ?? null;
  const value = probe?.current?.value ?? null;
  const state = value !== null && device ? soilMoistureLabel(value, device.thresholds.soil) : null;
  const color = 'var(--domiot-metric-soil)';
  const updatedAt = probe?.current?.recordedAt ?? latestAt;

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/app/dashboard" />
          </IonButtons>
          <IonTitle>{probe?.name ?? 'Humedad de suelo'}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen className="ion-padding">
        {loading && !overview && (
          <div className="ion-text-center ion-padding">
            <IonSpinner color="primary" />
          </div>
        )}
        {error && !overview && (
          <IonText color="danger">
            <p>{error}</p>
          </IonText>
        )}
        {overview && !probe && (
          <IonText color="medium">
            <p>Esta estación no tiene la sonda {channel} (o está desactivada).</p>
          </IonText>
        )}

        {overview && probe && (
          <>
            <IonCard className="station-detail-hero metric-soil">
              <IonCardContent>
                <div className="station-hero-top">
                  <span className="station-hero-name">
                    <IonIcon icon={leafOutline} />
                    {device?.name}
                  </span>
                  {updatedAt && <span className="station-source">Actualizado {formatRelative(updatedAt)}</span>}
                </div>
                {device && <ConnectivityBanner device={device} />}
                <div className="station-detail-now">
                  <div>
                    <span className="station-detail-value">{value !== null ? <CountUp value={String(round1(value))} /> : '—'}</span>
                    <span className="station-detail-unit">% humedad de suelo</span>
                    {state && <p className={`station-hero-sub station-tile-sub level-${state.level}`}>{state.label}</p>}
                  </div>
                </div>
              </IonCardContent>
            </IonCard>

            <IonSegment
              value={range}
              onIonChange={(e) => setRange(e.detail.value as StationRange)}
              className="station-range"
            >
              {(Object.keys(STATION_RANGE_LABELS) as StationRange[]).map((r) => (
                <IonSegmentButton value={r} key={r}>
                  <IonLabel>{STATION_RANGE_LABELS[r]}</IonLabel>
                </IonSegmentButton>
              ))}
            </IonSegment>

            <IonCard>
              <IonCardContent>
                <div className="station-section-head">
                  <span>{RANGE_TITLE[range]}</span>
                  {rangeLoading && <IonSpinner name="dots" className="station-inline-spinner" />}
                </div>
                <BandChart buckets={probe.series} color={color} unit="%" range={range} domain={[0, 80]} timeline={timelineOf(overview)} />
                <OutageLegend timeline={timelineOf(overview)} />
                <StatsGrid cells={soilStats(probe, range)} />
              </IonCardContent>
            </IonCard>

            <IonCard>
              <IonCardContent>
                <div className="station-section-head">
                  <span>Próximos 7 días</span>
                  <span className="station-source">vía Open-Meteo</span>
                </div>
                {forecastStatus === 'unset' && (
                  <p className="station-empty">Cargá la ubicación de la estación para ver el pronóstico.</p>
                )}
                {forecastStatus === 'error' && <p className="station-empty">No se pudo obtener el pronóstico.</p>}
                {forecastStatus === 'loading' && (
                  <IonSkeletonText
                    animated
                    style={{
                      width: '100%',
                      height: 160,
                      borderRadius: 10,
                      margin: 0,
                    }}
                  />
                )}
                {forecastStatus === 'ok' && forecast && (
                  <>
                    <WaterBalance forecast={forecast} />
                    <p className="station-footnote">
                      <IonIcon icon={pulseOutline} style={{ verticalAlign: 'middle' }} /> Si la evapotranspiración
                      supera a la lluvia, el suelo se va a ir secando.
                    </p>
                  </>
                )}
              </IonCardContent>
            </IonCard>
          </>
        )}
      </IonContent>
    </IonPage>
  );
};

const WaterBalance: React.FC<{ forecast: AgroForecast }> = ({ forecast }) => {
  const rain7 = forecast.daily.reduce((acc, d) => acc + d.precipitationSum, 0);
  const et07 = forecast.daily.reduce((acc, d) => acc + d.et0, 0);
  const balance = rain7 - et07;
  return (
    <div className="station-balance">
      <div>
        <p className="station-stat-label">Lluvia prevista</p>
        <p className="station-stat-value">{round1(rain7)} mm</p>
      </div>
      <div>
        <p className="station-stat-label">Evapotranspiración</p>
        <p className="station-stat-value">{round1(et07)} mm</p>
      </div>
      <div>
        <p className="station-stat-label">Balance hídrico</p>
        <p className={`station-stat-value ${balance < 0 ? 'negative' : 'positive'}`}>
          {balance > 0 ? '+' : ''}
          {round1(balance)} mm
        </p>
      </div>
    </div>
  );
};

const StationMetricDetail: React.FC = () => {
  const { id, metric, channel } = useParams<{
    id: string;
    metric?: string;
    channel?: string;
  }>();
  if (channel !== undefined) return <SoilDetail id={id} channel={Number(channel) || 1} />;
  return <MetricDetail id={id} metric={metric ?? 'temperatura'} />;
};

const MetricDetail: React.FC<{ id: string; metric: string }> = ({ id, metric }) => {
  const slug: StationDetailSlug = (STATION_DETAIL_SLUGS as string[]).includes(metric)
    ? (metric as StationDetailSlug)
    : 'temperatura';
  const meta = STATION_DETAIL_META[slug];
  const { device, overview, range, setRange, loading, rangeLoading, error, forecast, forecastStatus, latestAt } =
    useWeatherStation(id);

  const hero = overview ? heroFor(slug, overview) : null;
  const t = overview?.current.temperature?.value ?? null;
  const rh = overview?.current.humidity?.value ?? null;
  const w = overview?.current.windSpeed?.value ?? null;
  const insights =
    overview && device
      ? stationInsights(
          t,
          rh,
          w,
          overview.rain.lastHour,
          overview.stats.temperature?.min ?? null,
          device.thresholds,
          forecast?.hourly
        ).filter((i) =>
          slug === 'temperatura'
            ? i.kind === 'frost'
            : slug === 'humedad'
              ? i.kind !== 'frost'
              : slug === 'viento'
                ? i.kind === 'spray'
                : false
        )
      : [];

  const rainDaily = overview ? (range === '7d' ? overview.rain.daily.slice(-7) : overview.rain.daily) : [];
  const timeline = overview ? timelineOf(overview) : undefined;

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/app/dashboard" />
          </IonButtons>
          <IonTitle>{meta.title}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen className="ion-padding">
        {loading && !overview && (
          <div className="ion-text-center ion-padding">
            <IonSpinner color="primary" />
          </div>
        )}
        {error && !overview && (
          <IonText color="danger">
            <p>{error}</p>
          </IonText>
        )}

        {overview && hero && (
          <>
            <IonCard className={`station-detail-hero ${METRIC_CLASS[slug]}`}>
              <IonCardContent>
                <div className="station-hero-top">
                  <span className="station-hero-name">
                    <IonIcon icon={ICON_BY_SLUG[slug]} />
                    {device?.name}
                  </span>
                  {latestAt && <span className="station-source">Actualizado {formatRelative(latestAt)}</span>}
                </div>
                {device && <ConnectivityBanner device={device} />}
                <div className="station-detail-now">
                  <div>
                    <span className="station-detail-value">
                      <CountUp value={String(hero.value)} />
                    </span>
                    <span className="station-detail-unit">{hero.unit}</span>
                    {hero.sub && <p className="station-hero-sub">{hero.sub}</p>}
                  </div>
                  {slug === 'viento' && (
                    <WindCompass direction={overview.current.windDirection?.value ?? null} size={112} />
                  )}
                </div>
              </IonCardContent>
            </IonCard>

            {slug === 'lluvia' && (
              <div className="station-rain-totals">
                {[
                  ['Última hora', overview.rain.lastHour],
                  ['Hoy', overview.rain.today],
                  ['7 días', overview.rain.last7d],
                  ['30 días', overview.rain.last30d],
                ].map(([label, mm]) => (
                  <div className="station-rain-total" key={label as string}>
                    <span className="station-rain-total-value">{mm}</span>
                    <span className="station-rain-total-unit">mm</span>
                    <span className="station-rain-total-label">{label}</span>
                  </div>
                ))}
              </div>
            )}

            <StationInsights
              items={insights}
              settingsHref={device && device.access !== 'view' ? `/app/devices/${id}/thresholds` : undefined}
              customized={device?.customThresholds}
            />

            <IonSegment
              value={range}
              onIonChange={(e) => setRange(e.detail.value as StationRange)}
              className="station-range"
            >
              {(Object.keys(STATION_RANGE_LABELS) as StationRange[]).map((r) => (
                <IonSegmentButton value={r} key={r}>
                  <IonLabel>{STATION_RANGE_LABELS[r]}</IonLabel>
                </IonSegmentButton>
              ))}
            </IonSegment>

            <IonCard>
              <IonCardContent>
                <div className="station-section-head">
                  <span>{RANGE_TITLE[range]}</span>
                  {rangeLoading && <IonSpinner name="dots" className="station-inline-spinner" />}
                </div>
                {error && (
                  <IonText color="danger">
                    <p style={{ fontSize: 12.5, marginTop: 0 }}>{error}</p>
                  </IonText>
                )}
                {slug === 'temperatura' && (
                  <BandChart buckets={overview.series.temperature} color={meta.color} unit="°C" range={range} timeline={timeline} />
                )}
                {slug === 'presion' && (
                  <BandChart buckets={overview.series.pressure} color={meta.color} unit="hPa" range={range} timeline={timeline} />
                )}
                {slug === 'humedad' && (
                  <BandChart
                    buckets={overview.series.humidity}
                    color={meta.color}
                    unit="%"
                    range={range}
                    domain={[0, 100]}
                    timeline={timeline}
                  />
                )}
                {slug === 'lluvia' &&
                  (isHourlyRange(range) ? (
                    <RainBars buckets={overview.series.rain} color={meta.color} range={range} />
                  ) : (
                    <RainBars daily={rainDaily} color={meta.color} range={range} />
                  ))}
                {slug === 'viento' && (
                  <WindChart buckets={overview.series.windSpeed} color={meta.color} range={range} timeline={timeline} />
                )}
                <OutageLegend timeline={timeline} />
                <StatsGrid cells={statsFor(slug, overview)} />
              </IonCardContent>
            </IonCard>

            {slug === 'viento' && (
              <IonCard>
                <IonCardContent>
                  <div className="station-section-head">
                    <span>Rosa de los vientos · {STATION_RANGE_LABELS[range]}</span>
                  </div>
                  <div className="station-rose-wrap">
                    <WindRose sectors={overview.windRose} />
                  </div>
                  <p className="station-footnote">
                    Cada pétalo es el % del tiempo que el viento vino desde ese rumbo; más intenso, más fuerte sopló.
                  </p>
                </IonCardContent>
              </IonCard>
            )}

            {/* El pronóstico de 7 días está solo en el Inicio; acá, el de las
                próximas horas para esta métrica (la presión no tiene). */}
            {slug !== 'presion' && (
              <IonCard>
                <IonCardContent>
                  <div className="station-section-head">
                    <span>Próximas horas</span>
                    <span className="station-source">vía Open-Meteo</span>
                  </div>
                  <ForecastSection slug={slug} forecast={forecast} status={forecastStatus} />
                </IonCardContent>
              </IonCard>
            )}
          </>
        )}
      </IonContent>
    </IonPage>
  );
};

export default StationMetricDetail;
